import React from "react";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { useTranslation } from "react-i18next";
import {
  getClassLabelFromAkeneoObject,
  getSecondaryClassLabelFromAkeneoObject,
} from "../utils/akeneoUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import styled from "styled-components";
import { useMaterials } from "../hooks/useMaterials";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Control, Controller, useFieldArray } from "react-hook-form";
import { isInteger } from "../utils/rowUtils";
import {
  MaterialFormDataWithSpecificCodes,
  SecondaryMaterials,
} from "../types/materialForm.types";
import { InfoDot } from "./infoDot";
import { Button } from "primereact/button";
import { InputInstructionsHover } from "./inputInstructionsHover";

interface MaterialTabProps {
  ecomodClasses: EcomodClass[];
  control: Control<MaterialFormDataWithSpecificCodes, any>;
  watch?: any;
  allEcomodClasses: EcomodClass[];
  validationErrorFields?: {
    fieldPath: string;
    message: string;
  }[];
  tabCode: string;
}

const MaterialTabContainer = styled.div`
  padding: 1rem;

  & h3 {
    margin-top: 0;
    margin-bottom: 2rem;
    font-weight: 500;
    font-size: 1.2rem;
  }

  & strong {
    font-weight: 500;
  }

  & .p-inputtext,
  & .p-inputnumber-input {
    min-width: 150px;
    font-size: 0.8rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 1fr 1fr 1fr 1fr;
  gap: 0 1rem;
  width: 100%;
  margin-bottom: 2rem;
`;

const Header = styled.div<{
  $span?: number;
  $strong?: boolean;
  $wrap?: boolean;
}>`
  font-weight: ${(props) => (props.$strong ? "500" : "400")};
  grid-column: span ${(props) => props.$span || 1};
  white-space: ${(props) => (props.$wrap ? "normal" : "nowrap")};
  font-size: 0.9rem;
  min-height: 36px;
  display: flex;
  align-items: center;
`;

const LabelCell = styled.div`
  font-size: 0.8rem;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  padding-right: 0.5rem;
  max-width: 300px;
`;

const InputCell = styled.div<{ $gray?: boolean; $hasError?: boolean }>`
  font-size: 0.8rem;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${(props) => (props.$gray ? "#f2f2f2" : "transparent")};
  border-radius: var(--border-radius);
  padding: 0.5rem;

  & .p-inputtext,
  & .p-inputnumber-input {
    border: ${(props) =>
      props.$hasError ? "2px solid #ef4444" : "1px solid #ccc"};
    border-radius: var(--border-radius);

    &:focus {
      border-color: ${(props) =>
        props.$hasError ? "var(--sumi-error-color)" : "var(--primary-color)"};
      box-shadow: ${(props) =>
        props.$hasError
          ? "0 0 0 2px rgba(239, 68, 68, 0.2)"
          : "0 0 0 0.2rem rgba(72, 153, 255, 0.2)"};
    }
  }
`;

const GridBoxHeader = styled.div`
  font-size: 0.8rem;
  font-weight: 400;
  white-space: nowrap;
  color: #797979;
  margin-bottom: 0.3rem;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
  margin: 2rem 0;
`;

const SectionContainer = styled.div`
  position: relative;
`;

const SecondaryMatButton = styled.a`
  font-size: 0.8rem;
  text-align: right;
  text-decoration: underline;
  cursor: pointer;
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 2rem;
`;

const RemoveButton = styled(Button)`
  font-size: 0.8rem;
  padding: 0.2rem 0;
  width: 15px;
  margin-left: 5px;
`;

const MaterialTab: React.FC<MaterialTabProps> = ({
  ecomodClasses,
  control,
  watch,
  allEcomodClasses,
  validationErrorFields = [],
  tabCode,
}) => {
  const { t } = useTranslation();
  const [locale, toast] = useUIStore(
    useShallow((state: UIState) => [state.locale, state.toastRef]),
  );

  const consumerPlasticBags = ["2A2", "2A3", "2D2", "2D3"];
  const woodCode = "5";
  const multiMaterialInstructionKey =
    "input-instructions.multi-material-main-material-only";

  // Helper function to check if a field has validation errors
  const hasFieldError = (fieldName: string) => {
    return validationErrorFields.some(
      ({ fieldPath }) => fieldPath === fieldName,
    );
  };

  const getFieldErrorMessage = (fieldName: string) => {
    return validationErrorFields.find(
      ({ fieldPath }) => fieldPath === fieldName,
    )?.message;
  };

  const filteredOutMainEcomodClasses = ["1", "2", "3", "4", "5", "6"];
  const { multiMaterialPackageCodes, businessOnlyMultimaterialPackageCodes } =
    useMaterials();

  // Use useFieldArray for managing secondary materials arrays
  const consumerSecondaryFields = useFieldArray({
    control,
    name: "consumerPackaging.secondaryMaterials",
  });

  const businessSecondaryFields = useFieldArray({
    control,
    name: "businessPackaging.secondaryMaterials",
  });

  // Handler for adding secondary material
  const handleAddSecondaryMaterial = (
    packageType: "consumer" | "business",
    ecomodClassCode: string,
  ) => {
    // Check if this is a business-only multi-material code
    const isBusinessOnlyCode =
      businessOnlyMultimaterialPackageCodes.includes(ecomodClassCode);

    // If it's a business-only code and we're trying to add to consumer, prevent it
    if (isBusinessOnlyCode && packageType === "consumer") {
      console.warn(
        `Cannot add secondary material for business-only code ${ecomodClassCode} to consumer section`,
      );
      return;
    }

    const isMultiMaterial =
      multiMaterialPackageCodes.includes(ecomodClassCode) || isBusinessOnlyCode;

    const hasValues = watch
      ? (() => {
          const singleUsePackagedInFinland = watch(
            `${packageType}Packaging.singleUse.${isMultiMaterial ? "multiMaterial." : ""}packagedInFinland.${ecomodClassCode}`,
          );
          const singleUseImportedPackages = watch(
            `${packageType}Packaging.singleUse.${isMultiMaterial ? "multiMaterial." : ""}importedPackages.${ecomodClassCode}`,
          );
          const reusableFirstUsage = watch(
            `${packageType}Packaging.reusable.${isMultiMaterial ? "multiMaterial." : ""}firstUsage.${ecomodClassCode}`,
          );
          const reusableImportedPackages = watch(
            `${packageType}Packaging.reusable.${isMultiMaterial ? "multiMaterial." : ""}importedPackages.${ecomodClassCode}`,
          );
          const reusableReused = watch(
            `${packageType}Packaging.reusable.${isMultiMaterial ? "multiMaterial." : ""}reused.${ecomodClassCode}`,
          );

          return (
            singleUsePackagedInFinland ||
            singleUseImportedPackages ||
            reusableFirstUsage ||
            reusableImportedPackages ||
            reusableReused
          );
        })()
      : false;

    if (!hasValues) {
      toast?.current?.show({
        severity: "warn",
        detail: t("add_secondary_material_first"),
        life: 3000,
      });
      return;
    }

    // Get available ecomod materials (basic materials with integer codes)
    const availableEcomodMaterials = allEcomodClasses?.filter((cls) =>
      isInteger(cls.code as string),
    );

    if (!availableEcomodMaterials || availableEcomodMaterials.length === 0) {
      console.warn("No available ecomod materials found");
      return;
    }

    // Initialize with the first available material as default
    // Should always contain 7+ materials, so this is safe even if the material from tab is filtered out
    const defaultMaterial = availableEcomodMaterials.filter(
      (cls) => cls.code !== tabCode,
    )[0];

    const newSecondaryMaterial: SecondaryMaterials = {
      materialCode: defaultMaterial.code || "",
      ecoModulationClassCode: ecomodClassCode,
      singleUse: {
        packagedInFinland: "",
        importedPackages: "",
      },
      reusable: {
        firstUsage: "",
        importedPackages: "",
        reused: "",
      },
    };

    // Use the proper useFieldArray append method
    if (packageType === "consumer") {
      consumerSecondaryFields.append(newSecondaryMaterial);
    } else {
      businessSecondaryFields.append(newSecondaryMaterial);
    }
  };

  const handleRemoveSecondaryMaterial = (
    packageType: "consumer" | "business",
    index: number,
  ) => {
    if (packageType === "consumer") {
      consumerSecondaryFields.remove(index);
    } else {
      businessSecondaryFields.remove(index);
    }
  };

  // Split all materials into single and multi material types
  const allConsumerSingle = ecomodClasses
    .filter(
      (cls) =>
        !multiMaterialPackageCodes.includes(cls.code || "") &&
        // Exclude industrial materials - "teollisuuden"
        !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
        // Exclude stamped pallets (with code '5A1')
        cls.code !== "5A1" &&
        !filteredOutMainEcomodClasses.includes(cls.code || ""),
    )
    .sort((a, b) => {
      const aIsPlasticBag = consumerPlasticBags.includes(a.code || "");
      const bIsPlasticBag = consumerPlasticBags.includes(b.code || "");

      if (aIsPlasticBag && !bIsPlasticBag) return 1;
      if (!aIsPlasticBag && bIsPlasticBag) return -1;
      return 0;
    });

  const allConsumerMulti = ecomodClasses
    .filter(
      (cls) =>
        multiMaterialPackageCodes.includes(cls.code || "") &&
        !cls.nameFi?.toLowerCase().includes("teollisuuden") &&
        !filteredOutMainEcomodClasses.includes(cls.code || ""),
    )
    .sort((a, b) => (a.nameFi || "").localeCompare(b.nameFi || ""));

  const allBusinessSingle = ecomodClasses.filter(
    (cls) =>
      !multiMaterialPackageCodes.includes(cls.code || "") &&
      !businessOnlyMultimaterialPackageCodes.includes(cls.code || "") &&
      !consumerPlasticBags.includes(cls.code || "") &&
      !filteredOutMainEcomodClasses.includes(cls.code || ""),
  );

  const allBusinessMulti = ecomodClasses
    .filter(
      (cls) =>
        (multiMaterialPackageCodes.includes(cls.code || "") ||
          businessOnlyMultimaterialPackageCodes.includes(cls.code || "")) &&
        !filteredOutMainEcomodClasses.includes(cls.code || ""),
    )
    .sort((a, b) => (a.nameFi || "").localeCompare(b.nameFi || ""));

  // Get available secondary materials
  const availableSecondaryMaterials = allEcomodClasses
    .filter((cls) => isInteger(cls.code as string) && cls.code !== tabCode)
    .map((cls) => ({
      label: getSecondaryClassLabelFromAkeneoObject(locale, cls),
      value: cls.code,
      cls: cls,
    }));

  // Helper function to get secondary materials for a specific ecomod class
  const getSecondaryMaterialsForEcomod = (
    packageType: "consumer" | "business",
    ecomodClassCode: string,
  ) => {
    const secondaryMaterials =
      packageType === "consumer"
        ? consumerSecondaryFields.fields
        : businessSecondaryFields.fields;

    if (!secondaryMaterials) return [];

    return secondaryMaterials.filter(
      (secMat) => secMat.ecoModulationClassCode === ecomodClassCode,
    );
  };

  // Builds a per-field translation key so single material tooltips can be overridden individually
  const getSingleMaterialInstructionKey = (
    packageType: "consumer" | "business",
    code: string,
    section: "singleUse" | "reusable",
    field: string,
  ) => `input-instructions.${packageType}-${code}-${section}-${field}`;

  // Render single materials grid
  const renderSingleMaterialsGrid = (
    packageType: "consumer" | "business",
    materials: EcomodClass[],
  ) => {
    if (materials.length === 0) return null;

    return (
      <Grid>
        {/* Headers */}
        <Header $strong={true}>
          <InfoDot
            position="right"
            text={t("infotext-single-materials")}
            content={
              <div>
                <p>{t("infotext-single-materials-part-1")}</p>
                <p>{t("infotext-single-materials-part-2")}</p>
                <p>{t("infotext-single-materials-part-3")}</p>
              </div>
            }
            marginLeft="10px"
          >
            {t("label-single-materials")}
          </InfoDot>
        </Header>

        <Header $span={2}>
          <InfoDot
            position="right"
            text={t("infotext-single-use-packaging")}
            marginLeft="10px"
          >
            {t("header-single-use-packaging")}
          </InfoDot>
        </Header>
        <Header $span={3}>
          <InfoDot
            position="right"
            text=""
            content={
              <div>
                <p>{t("infotext-reusable-packaging-part-1")}</p>
                <p>{t("infotext-reusable-packaging-part-2")}</p>
                <p>{t("infotext-reusable-packaging-part-3")}</p>
                <p>{t("infotext-reusable-packaging-part-4")}</p>
              </div>
            }
            marginLeft="10px"
          >
            {t("header-reusable-packaging")}
          </InfoDot>
        </Header>

        {/* Column headers */}
        <div />
        <InputCell $gray={true}>
          <GridBoxHeader>{t("header-packaged-in-finland")}</GridBoxHeader>
        </InputCell>
        <InputCell $gray={false}>
          <GridBoxHeader>{t("header-imported-packages")}</GridBoxHeader>
        </InputCell>
        <InputCell $gray={true}>
          <GridBoxHeader>{t("header-1st-usage")}</GridBoxHeader>
        </InputCell>
        <InputCell $gray={false}>
          <GridBoxHeader>{t("header-imported-packages")}</GridBoxHeader>
        </InputCell>
        <InputCell $gray={true}>
          <GridBoxHeader>{t("header-reused")}</GridBoxHeader>
        </InputCell>

        {/* Material rows */}
        {materials.map((cls) => (
          <React.Fragment key={cls.code}>
            {/* Label */}
            <LabelCell>
              <span>{getClassLabelFromAkeneoObject(locale, cls)}</span>
            </LabelCell>

            {/* Single Use - Packaged in Finland */}
            <InputCell $gray={true}>
              <Controller
                name={`${packageType}Packaging.singleUse.packagedInFinland.${cls.code}`}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <InputInstructionsHover
                    translationKey={getSingleMaterialInstructionKey(
                      packageType,
                      cls.code as string,
                      "singleUse",
                      "packagedInFinland",
                    )}
                  >
                    <InputNumber
                      {...field}
                      onChange={(e) => field.onChange(e.value)}
                      value={parseFloat(field.value) || undefined}
                      suffix=" kg"
                      useGrouping={false}
                      locale="fi-FI"
                    />
                  </InputInstructionsHover>
                )}
              />
            </InputCell>

            {/* Single Use - Imported Packages */}
            {!consumerPlasticBags.includes(cls.code || "") ? (
              <InputCell $gray={false}>
                <Controller
                  name={`${packageType}Packaging.singleUse.importedPackages.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={getSingleMaterialInstructionKey(
                        packageType,
                        cls.code as string,
                        "singleUse",
                        "importedPackages",
                      )}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>
            ) : (
              <InputCell $gray={false} />
            )}

            {/* Reusable - First Usage */}
            {!consumerPlasticBags.includes(cls.code || "") ? (
              <InputCell $gray={true}>
                <Controller
                  name={`${packageType}Packaging.reusable.firstUsage.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={getSingleMaterialInstructionKey(
                        packageType,
                        cls.code as string,
                        "reusable",
                        "firstUsage",
                      )}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>
            ) : (
              <InputCell $gray={false} />
            )}

            {/* Reusable - Imported Packages */}
            {!consumerPlasticBags.includes(cls.code || "") ? (
              <InputCell $gray={false}>
                <Controller
                  name={`${packageType}Packaging.reusable.importedPackages.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={getSingleMaterialInstructionKey(
                        packageType,
                        cls.code as string,
                        "reusable",
                        "importedPackages",
                      )}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>
            ) : (
              <InputCell $gray={false} />
            )}

            {/* Reusable - Reused */}
            {!consumerPlasticBags.includes(cls.code || "") ? (
              <InputCell $gray={true}>
                <Controller
                  name={`${packageType}Packaging.reusable.reused.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={getSingleMaterialInstructionKey(
                        packageType,
                        cls.code as string,
                        "reusable",
                        "reused",
                      )}
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>
            ) : (
              <InputCell $gray={false} />
            )}
          </React.Fragment>
        ))}
      </Grid>
    );
  };

  // Render multi materials grid
  const renderMultiMaterialsGrid = (
    packageType: "consumer" | "business",
    materials: EcomodClass[],
  ) => {
    if (materials.length === 0) return null;

    return (
      <Grid>
        {/* Headers */}
        <InfoDot
          position="right"
          text=""
          content={
            <div>
              <p>{t("infotext-multi-materials-part-1")}</p>
              <p>{t("infotext-multi-materials-part-2")}</p>
              <p>{t("infotext-multi-materials-part-3")}</p>
              <p>{t("infotext-multi-materials-part-4")}</p>
            </div>
          }
        >
          <Header
            className="headerwtf"
            $strong={true}
            $wrap={
              packageType === "business" && tabCode === woodCode ? true : false
            }
          >
            {packageType === "business" && tabCode === woodCode
              ? t("label-single-or-multi-material")
              : t("label-multi-materials")}
          </Header>
        </InfoDot>
        <Header $span={2}>{t("header-single-use-packaging")}</Header>
        <Header $span={3}>{t("header-reusable-packaging")}</Header>

        {/* Column headers */}
        <div />
        <InputCell
          $gray={true}
          style={{ background: "transparent", marginTop: "-20px" }}
        >
          <GridBoxHeader></GridBoxHeader>
        </InputCell>
        <InputCell
          $gray={false}
          style={{ background: "transparent", marginTop: "-20px" }}
        >
          <GridBoxHeader></GridBoxHeader>
        </InputCell>
        <InputCell
          $gray={true}
          style={{ background: "transparent", marginTop: "-20px" }}
        >
          <GridBoxHeader></GridBoxHeader>
        </InputCell>
        <InputCell
          $gray={false}
          style={{ background: "transparent", marginTop: "-20px" }}
        >
          <GridBoxHeader></GridBoxHeader>
        </InputCell>
        <InputCell
          $gray={true}
          style={{ background: "transparent", marginTop: "-20px" }}
        >
          <GridBoxHeader></GridBoxHeader>
        </InputCell>

        {/* Material rows */}
        {materials.map((cls) => {
          const secondaryMats = getSecondaryMaterialsForEcomod(
            packageType,
            cls.code as string,
          );

          return (
            <React.Fragment key={cls.code}>
              {/* Main material row */}
              <LabelCell>
                <span>{getClassLabelFromAkeneoObject(locale, cls)}</span>
              </LabelCell>

              {/* Single Use - Packaged in Finland */}
              <InputCell
                $gray={true}
                $hasError={hasFieldError(
                  `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${cls.code}`,
                )}
              >
                <GridBoxHeader>{t("header-packaged-in-finland")}</GridBoxHeader>
                <Controller
                  name={`${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={
                        tabCode === woodCode
                          ? undefined
                          : multiMaterialInstructionKey
                      }
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                        tooltip={
                          hasFieldError(
                            `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${cls.code}`,
                          )
                            ? getFieldErrorMessage(
                                `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${cls.code}`,
                              )
                            : undefined
                        }
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Single Use - Imported Packages */}
              <InputCell
                $gray={false}
                $hasError={hasFieldError(
                  `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${cls.code}`,
                )}
              >
                <GridBoxHeader>{t("header-imported-packages")}</GridBoxHeader>
                <Controller
                  name={`${packageType}Packaging.singleUse.multiMaterial.importedPackages.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={
                        tabCode === woodCode
                          ? undefined
                          : multiMaterialInstructionKey
                      }
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                        tooltip={
                          hasFieldError(
                            `${packageType}Packaging.reusable.multiMaterial.firstUsage.${cls.code}`,
                          )
                            ? getFieldErrorMessage(
                                `${packageType}Packaging.reusable.multiMaterial.firstUsage.${cls.code}`,
                              )
                            : undefined
                        }
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Reusable - First Usage */}
              <InputCell
                $gray={true}
                $hasError={hasFieldError(
                  `${packageType}Packaging.reusable.multiMaterial.firstUsage.${cls.code}`,
                )}
              >
                <GridBoxHeader>{t("header-1st-usage")}</GridBoxHeader>
                <Controller
                  name={`${packageType}Packaging.reusable.multiMaterial.firstUsage.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={
                        tabCode === woodCode
                          ? packageType === "business"
                            ? getSingleMaterialInstructionKey(
                                packageType,
                                cls.code as string,
                                "reusable",
                                "firstUsage",
                              )
                            : undefined
                          : multiMaterialInstructionKey
                      }
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                        tooltip={
                          hasFieldError(
                            `${packageType}Packaging.reusable.multiMaterial.importedPackages.${cls.code}`,
                          )
                            ? getFieldErrorMessage(
                                `${packageType}Packaging.reusable.multiMaterial.importedPackages.${cls.code}`,
                              )
                            : undefined
                        }
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Reusable - Imported Packages */}
              <InputCell
                $gray={false}
                $hasError={hasFieldError(
                  `${packageType}Packaging.reusable.multiMaterial.importedPackages.${cls.code}`,
                )}
              >
                <GridBoxHeader>{t("header-imported-packages")}</GridBoxHeader>
                <Controller
                  name={`${packageType}Packaging.reusable.multiMaterial.importedPackages.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={
                        tabCode === woodCode
                          ? packageType === "business"
                            ? getSingleMaterialInstructionKey(
                                packageType,
                                cls.code as string,
                                "reusable",
                                "importedPackages",
                              )
                            : undefined
                          : multiMaterialInstructionKey
                      }
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                        tooltip={
                          hasFieldError(
                            `${packageType}Packaging.reusable.multiMaterial.reused.${cls.code}`,
                          )
                            ? getFieldErrorMessage(
                                `${packageType}Packaging.reusable.multiMaterial.reused.${cls.code}`,
                              )
                            : undefined
                        }
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Reusable - Reused */}
              <InputCell
                $gray={true}
                $hasError={hasFieldError(
                  `${packageType}Packaging.reusable.multiMaterial.reused.${cls.code}`,
                )}
              >
                <GridBoxHeader>{t("header-reused")}</GridBoxHeader>
                <Controller
                  name={`${packageType}Packaging.reusable.multiMaterial.reused.${cls.code}`}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <InputInstructionsHover
                      translationKey={
                        tabCode === woodCode
                          ? packageType === "business"
                            ? getSingleMaterialInstructionKey(
                                packageType,
                                cls.code as string,
                                "reusable",
                                "reused",
                              )
                            : undefined
                          : multiMaterialInstructionKey
                      }
                    >
                      <InputNumber
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                        value={parseFloat(field.value) || undefined}
                        suffix=" kg"
                        useGrouping={false}
                        locale="fi-FI"
                        tooltip={
                          hasFieldError(
                            `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${cls.code}`,
                          )
                            ? getFieldErrorMessage(
                                `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${cls.code}`,
                              )
                            : undefined
                        }
                      />
                    </InputInstructionsHover>
                  )}
                />
              </InputCell>

              {/* Secondary material rows */}
              {secondaryMats.map((secMat, secIndex) => {
                const actualIndex =
                  (packageType === "consumer"
                    ? consumerSecondaryFields.fields
                    : businessSecondaryFields.fields
                  )?.findIndex((sm) => sm === secMat) ?? -1;

                return (
                  <React.Fragment key={secMat.id}>
                    {/* Secondary material label (dropdown) */}
                    <LabelCell>
                      <Controller
                        name={
                          `[${packageType}Packaging.secondaryMaterials][${actualIndex}].materialCode` as any
                        }
                        control={control}
                        render={({ field }) => (
                          <div style={{ display: "flex" }}>
                            <Dropdown
                              options={availableSecondaryMaterials}
                              placeholder={t("placeholder-select-material")}
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e.value);
                              }}
                              style={{
                                minWidth: "300px",
                                maxWidth: "300px",
                              }}
                            />

                            <RemoveButton
                              icon="pi pi-times"
                              text
                              size="small"
                              type="button"
                              onClick={() =>
                                handleRemoveSecondaryMaterial(
                                  packageType,
                                  actualIndex,
                                )
                              }
                            ></RemoveButton>
                          </div>
                        )}
                      />
                    </LabelCell>

                    {/* Single Use - Packaged in Finland */}
                    <InputCell $gray={true}>
                      <Controller
                        name={
                          `${packageType}Packaging.secondaryMaterials[${actualIndex}].singleUse.packagedInFinland` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>

                    {/* Single Use - Imported Packages */}
                    <InputCell $gray={false}>
                      <Controller
                        name={
                          `${packageType}Packaging.secondaryMaterials[${actualIndex}].singleUse.importedPackages` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>

                    {/* Reusable - First Usage */}
                    <InputCell $gray={true}>
                      <Controller
                        name={
                          `${packageType}Packaging.secondaryMaterials[${actualIndex}].reusable.firstUsage` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>

                    {/* Reusable - Imported Packages */}
                    <InputCell $gray={false}>
                      <Controller
                        name={
                          `${packageType}Packaging.secondaryMaterials[${actualIndex}].reusable.importedPackages` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>

                    {/* Reusable - Reused */}
                    <InputCell $gray={true}>
                      <Controller
                        name={
                          `${packageType}Packaging.secondaryMaterials[${actualIndex}].reusable.reused` as any
                        }
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <InputNumber
                            {...field}
                            onChange={(e) => field.onChange(e.value)}
                            value={parseFloat(field.value) || undefined}
                            suffix=" kg"
                            useGrouping={false}
                            locale="fi-FI"
                          />
                        )}
                      />
                    </InputCell>
                  </React.Fragment>
                );
              })}

              {/* Add secondary material button row */}
              <LabelCell>
                <SecondaryMatButton
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleAddSecondaryMaterial(packageType, cls.code as string)
                  }
                >
                  {t("cmd-add-secondary-material")}
                </SecondaryMatButton>
              </LabelCell>
              <div />
              <div />
              <div />
              <div />
              <div />
            </React.Fragment>
          );
        })}
      </Grid>
    );
  };

  return (
    <MaterialTabContainer className="material-tab">
      {/* Consumer Packaging Section */}
      <SectionContainer>
        <h3>
          <InfoDot
            position="right"
            text={t("infotext-consumer-packaging")}
            marginLeft="10px"
          >
            {t("header-consumer-packaging")}
          </InfoDot>
        </h3>
        {renderSingleMaterialsGrid("consumer", allConsumerSingle)}
        {renderMultiMaterialsGrid("consumer", allConsumerMulti)}
      </SectionContainer>

      <SectionDivider />

      {/* Business Packaging Section */}
      <SectionContainer>
        <h3>
          <InfoDot
            position="right"
            text={t("infotext-business-packaging")}
            marginLeft="10px"
          >
            {t("header-business-packaging")}
          </InfoDot>
        </h3>
        {renderSingleMaterialsGrid("business", allBusinessSingle)}
        {renderMultiMaterialsGrid("business", allBusinessMulti)}
      </SectionContainer>
    </MaterialTabContainer>
  );
};

export default MaterialTab;
